import flatstore from "flatstore";
import { isNil } from "lodash";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SubscriptionCustomizeLoader from "../../../assets/contentLoader/SubscriptionCustomizeLoader";
import farmersImgchicken from "../../../assets/img/subscription/farmer_chicken.jpg";
import farmersImgcow from "../../../assets/img/subscription/farmer_cow.jpg";
import farmersImgfish from "../../../assets/img/subscription/farmer_fish.jpg";
import farmersImgpig from "../../../assets/img/subscription/farmer_pig.jpg";
import Image from "../../../components/common/Image";
import W from "../../../components/common/Word";
import {
  getSubscriptionPaths,
  localStorageUtil,
} from "../../../utils/functions";
import { getSubscriptionProductList } from "../../../utils/services";

const calculateTotalItems = (items) =>
  Object.values(items).reduce((total, item) => {
    // For partial gift items, only count the quantity above the gift amount
    // The gift portion doesn't count towards maxItems
    if (item.is_partial_gift) {
      const partialGiftAmount = parseInt(item.partial_gift_amount, 10) || 1;
      const regularQuantity = item.quantity - partialGiftAmount;
      return total + Math.max(0, regularQuantity);
    }
    return total + item.quantity;
  }, 0);

const itemSelectionReducer = (state, action) => {
  let newState = { ...state };
  switch (action.type) {
    case "add":
      // Ensure not to exceed maxItems
      if (newState.totalItems >= newState.maxItems) return newState;
      const currentItem = newState.items[action.itemId];
      const quantityAdd = currentItem
        ? currentItem.quantity + 1
        : 1;
      newState.items[action.itemId] = {
        product_id: action.productId,
        quantity: quantityAdd,
        title: action.title,
        weight: action.weight,
        unit_type: action.unit_type,
        pieces_per_vac: action.pieces_per_vac,
        subscription_amount: action.subscription_amount,
      };
      // Preserve gift flags if this is a partial gift item
      if (currentItem && currentItem.is_partial_gift) {
        newState.items[action.itemId].is_gift = currentItem.is_gift;
        newState.items[action.itemId].is_partial_gift = currentItem.is_partial_gift;
        newState.items[action.itemId].partial_gift_amount = currentItem.partial_gift_amount;
      }
      break;
    case "addMultiple":
      // Add multiple items at once (for URL prefilling)
      // prefilledItems represent TOTAL quantities, not additional quantities
      action.items.forEach((item) => {
        const currentItem = newState.items[item.itemId];
        const currentQuantity = currentItem?.quantity || 0;
        const requestedQuantity = item.quantity || 1;
        
        // Calculate how many items we can still add
        const remainingSlots = newState.maxItems - newState.totalItems;
        // Adjust for items that already exist (we're replacing, not adding)
        const slotsNeeded = requestedQuantity - currentQuantity;
        
        // Always update the item to ensure quantity is set correctly
        if (slotsNeeded <= remainingSlots) {
          newState.items[item.itemId] = {
            product_id: item.productId,
            quantity: requestedQuantity,
            title: item.title,
            weight: item.weight,
            unit_type: item.unit_type,
            pieces_per_vac: item.pieces_per_vac,
            subscription_amount: item.subscription_amount,
          };
          // Preserve gift flags if this is a partial gift item (from action or current state)
          if (item.is_partial_gift) {
            newState.items[item.itemId].is_gift = item.is_gift;
            newState.items[item.itemId].is_partial_gift = item.is_partial_gift;
            newState.items[item.itemId].partial_gift_amount = item.partial_gift_amount;
          } else if (currentItem && currentItem.is_partial_gift) {
            newState.items[item.itemId].is_gift = currentItem.is_gift;
            newState.items[item.itemId].is_partial_gift = currentItem.is_partial_gift;
            newState.items[item.itemId].partial_gift_amount = currentItem.partial_gift_amount;
          }
        }
      });
      break;
    case "remove":
      if (newState.items[action.itemId]) {
        const currentItem = newState.items[action.itemId];
        // For partial gift items, don't allow going below the partial gift amount
        if (currentItem.is_partial_gift) {
          const minQuantity = parseInt(currentItem.partial_gift_amount, 10) || 1;
          if (currentItem.quantity > minQuantity) {
            newState.items[action.itemId].quantity -= 1;
            // Preserve gift flags
            newState.items[action.itemId].is_gift = currentItem.is_gift;
            newState.items[action.itemId].is_partial_gift = currentItem.is_partial_gift;
            newState.items[action.itemId].partial_gift_amount = currentItem.partial_gift_amount;
          }
        } else {
          // Regular items: decrease by 1 or remove if quantity is 1
          if (currentItem.quantity > 1) {
            newState.items[action.itemId].quantity -= 1;
          } else {
            delete newState.items[action.itemId];
          }
        }
      }
      break;
    case "reset":
      newState.items = {};
      break;
    case "updateMaxItems":
      newState.maxItems = action.maxItems;
      break;
    default:
      return newState;
  }
  newState.totalItems = calculateTotalItems(newState.items);
  return newState;
};

function CustomizeSubscription({ subscription }) {
  const [data, setData] = useState([]);
  const [submissionNotice, setSubmissionNotice] = useState({
    message: "",
    className: "",
  });
  const [maxQtyMsgVisible, setMaxQtyMsgVisible] = useState({});
  const [isCountBoxOpen, setIsCountBoxOpen] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = getSubscriptionPaths();

  const size = localStorageUtil.getItem("subscription")?.size || "";
  const type = "customize";

  const [maxItemsReached, setMaxItemsReached] = useState(false);
  const [maxItemsLimit, setMaxItemsLimit] = useState(
    size === "small" ? 11 : 20
  );

  const isResetting =
    sessionStorage.getItem("resetting_subscription") === "true";
  
  // Track if URL items have been processed to prevent duplicate processing
  const urlItemsProcessedRef = useRef(false);

  // Initialize items from localStorage
  // Include partial gift items (they can be modified) but exclude full gift items
  const getInitialItems = () => {
    const subscriptionItems = localStorageUtil.getItem("subscription")?.items || {};
    // Include regular items and partial gift items (exclude only full gift items)
    const items = Object.keys(subscriptionItems).reduce((acc, itemId) => {
      const item = subscriptionItems[itemId];
      // Include regular items and partial gift items (exclude only full gift items)
      if (item?.is_gift !== true || item?.is_partial_gift === true) {
        acc[itemId] = item;
      }
      return acc;
    }, {});
    return items;
  };

  const [selectedItems, dispatch] = useReducer(
    itemSelectionReducer,
    {
      items: getInitialItems(),
      totalItems: 0,
      maxItems: maxItemsLimit,
    },
    (initState) => ({
      ...initState,
      totalItems: calculateTotalItems(initState.items),
    })
  );

  const animal_types = {
    bioCow: 1,
    cow: 2,
    chicken: 3,
    pig: 4,
    sheep: 5,
    duck: 6,
    fish: 7,
    deer: 8,
  };

  useEffect(() => {
    if (!size) {
      navigate(paths.base || "/");
    }

    // Preserve all gift items when saving - merge customize items with gift items
    // Full gift items are stored separately and don't count towards maxItems
    // Partial gift items are in selectedItems.items and can be modified
    const subscriptionItems = localStorageUtil.getItem("subscription")?.items || {};
    const fullGiftItems = Object.keys(subscriptionItems).reduce((acc, itemId) => {
      const item = subscriptionItems[itemId];
      // Keep only full gift items (not partial - those are in selectedItems.items)
      if (item?.is_gift === true && item?.is_partial_gift !== true) {
        acc[itemId] = item;
      }
      return acc;
    }, {});
    
    // Merge customize step items (which includes partial gift items) with full gift items
    // Partial gift items are already in selectedItems.items with their gift flags preserved
    const allItems = { ...selectedItems.items, ...fullGiftItems };
    
    localStorageUtil.updateObjectItem("subscription", {
      items: allItems,
    });
    
    // Update maxItemsReached based on totalItems and maxItems
    if (selectedItems.totalItems >= selectedItems.maxItems) {
      setMaxItemsReached(true);
    } else {
      setMaxItemsReached(false);
    }
  }, [selectedItems, data, size, navigate, paths]);

  useEffect(() => {
    if (isNil(subscription)) {
      getSubscriptionProductList();
    }
    if (subscription && !isNil(subscription)) {
      // Filter data: include regular items and partial gift items (exclude only full gift items)
      // Partial gift items can be selected by users, but they're separate from gift quantity
      let filteredData = subscription.filter((item) => {
        const isFullGift = item.is_gift === "yes" && item.is_partial_gift !== "yes";
        return (
          item.subscription_type === "grutto_subscription_custom_" + size &&
          item.total_stock !== 0 &&
          !isFullGift // Exclude only full (non-partial) gift items
        );
      });

      // Map animal types according to mapping
      const animalTypeMap = {
        boar: "game",
        roe: "game",
        deer: "game",
        salmon: "fish",
        codfish: "fish",
      };

      filteredData = filteredData.map((item) => ({
        ...item,
        animal_type: animalTypeMap[item.animal_type] || item.animal_type,
      }));

      // Sort filteredData based on animal_type
      filteredData.sort((a, b) =>
        a.animal_type === b.animal_type
          ? 0
          : animal_types[a.animal_type] > animal_types[b.animal_type]
          ? 1
          : -1
      );

      setData(filteredData);

      if (typeof filteredData[0]?.bundle_size !== "undefined") {
        // Use bundle_size directly - gift items don't count towards maxItems
        let maxItemsNum = Number(filteredData[0].bundle_size);
        
        setMaxItemsLimit(maxItemsNum);
        dispatch({ type: "updateMaxItems", maxItems: maxItemsNum });
      }

      // Read items from prefilledItems (stored temporarily from URL) and process them
      const prefilledItems = localStorageUtil.getItem("subscription")?.prefilledItems || null;
      if (prefilledItems && !urlItemsProcessedRef.current && filteredData.length > 0) {
        // Mark as processed IMMEDIATELY to prevent duplicate processing and reset interference
        urlItemsProcessedRef.current = true;
        
        // Collect all items to add in a batch
        // Skip full gift items - they should not be added via URL prefilling
        // But partial gift items can be prefilled (they're in the reducer state)
        const itemsToAdd = [];
        Object.entries(prefilledItems).forEach(([itemSlug, addedQuantity]) => {
          const dataItem = filteredData.find(
            (dataItem) => dataItem.slug === itemSlug
          );
          if (dataItem) {
            const itemId = dataItem.bundled_item_id;
            // Skip if this is a full gift item (partial gift items can be prefilled)
            const subscriptionItems = localStorageUtil.getItem("subscription")?.items || {};
            const existingItem = subscriptionItems[itemId];
            if (existingItem?.is_gift === true && existingItem?.is_partial_gift !== true) {
              return; // Skip full gift items
            }
            
            // For partial gift items, ensure quantity is at least the partial gift amount
            let finalQuantity = parseInt(addedQuantity, 10) || 1;
            if (existingItem && existingItem.is_partial_gift) {
              const minQuantity = parseInt(existingItem.partial_gift_amount, 10) || 1;
              finalQuantity = Math.max(finalQuantity, minQuantity);
            }
            
            // Add item with the correct quantity
            itemsToAdd.push({
              itemId: itemId,
              productId: dataItem.meatcut_product_id,
              quantity: finalQuantity,
              title: dataItem.title,
              weight: dataItem.weight,
              unit_type: dataItem.unit_type,
              pieces_per_vac: dataItem.pieces_per_vac,
              subscription_amount: dataItem.subscription_amount,
            });
          }
        });

        // Dispatch all items at once to avoid timing issues
        // For partial gift items, we need to preserve their gift flags
        if (itemsToAdd.length > 0) {
          // Get current items to preserve partial gift flags
          const currentSubscriptionItems = localStorageUtil.getItem("subscription")?.items || {};
          const itemsWithFlags = itemsToAdd.map((item) => {
            const existingItem = currentSubscriptionItems[item.itemId];
            if (existingItem && existingItem.is_partial_gift) {
              return {
                ...item,
                is_gift: existingItem.is_gift,
                is_partial_gift: existingItem.is_partial_gift,
                partial_gift_amount: existingItem.partial_gift_amount,
              };
            }
            return item;
          });
          
          dispatch({
            type: "addMultiple",
            items: itemsWithFlags,
          });
        }

        // Remove prefilledItems from localStorage after processing
        const subscriptionData = localStorageUtil.getItem("subscription") || {};
        delete subscriptionData.prefilledItems;
        localStorageUtil.updateObjectItem("subscription", subscriptionData);

        // Remove items parameters from URL after processing (only when resetting subscription)
        if (isResetting) {
          setTimeout(() => {
            const queryParams = new URLSearchParams(window.location.search);
            const newQueryParams = new URLSearchParams(window.location.search);
            for (const key of queryParams.keys()) {
              if (key.startsWith("items[")) {
                newQueryParams.delete(key);
              }
            }
            const newUrl = window.location.pathname + (newQueryParams.toString() ? "?" + newQueryParams.toString() : "");
            // Use replaceState for resetting subscriptions to clean up URL without creating extra history
            window.history.replaceState({}, "", newUrl);
          }, 200);
        }
      }
    }
  }, [size, subscription]);

  // hide submissionNotice after certain time
  useEffect(() => {
    let timeout;
    if (submissionNotice) {
      timeout = setTimeout(() => {
        setSubmissionNotice({ message: "", className: "" });
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [submissionNotice, setSubmissionNotice]);

  useEffect(() => {
    if (data.length > 0) {
      // Data is available, set up the references
      assignRefs();
    }
  }, [data]);

  const animalTypes = [...new Set(data.map((item) => item.animal_type))];
  const animalGroupRefs = useRef({});
  const assignRefs = () => {
    // Assign refs for each animal group element
    animalTypes.forEach((type) => {
      animalGroupRefs.current[type] = React.createRef();
    });
  };

  useEffect(() => {
    // Don't reset if we're still processing URL items or if data isn't ready
    if (!urlItemsProcessedRef.current || !data.length) {
      return;
    }

    // Check if all selected items exist in data (items might be from different size)
    const allItemsExist = Object.keys(selectedItems.items).every((itemKey) => {
      return data.some(
        (dataItem) => dataItem.bundled_item_id.toString() === itemKey
      );
    });

    // Reset if items exceed limit or don't exist in data (after URL items are processed)
    if (selectedItems.totalItems > maxItemsLimit || !allItemsExist) {
      dispatch({ type: "reset" });
    }
  }, [selectedItems, data, maxItemsLimit]);

  const updateMaxQtyMsgVisibility = (id, isVisible) => {
    setMaxQtyMsgVisible((prev) => ({
      ...prev,
      [id]: isVisible,
    }));
    if (isVisible) {
      setTimeout(() => {
        setMaxQtyMsgVisible((prev) => ({
          ...prev,
          [id]: false,
        }));
      }, 3000);
    }
  };

  const handleItemSelection = useCallback(
    (
      itemId,
      productId,
      maxQty,
      actionType,
      title,
      weight,
      unit_type,
      pieces_per_vac,
      subscription_amount,
    ) => {
      if (actionType === "add") {
        const currentItemQuantity = selectedItems.items[itemId]?.quantity ?? 0;
        if (currentItemQuantity >= parseInt(maxQty)) {
          updateMaxQtyMsgVisibility(itemId, true); // Show max quantity message
          return;
        }
      }
      // Otherwise, proceed with item selection
      if (
        actionType === "add" &&
        selectedItems.totalItems >= selectedItems.maxItems
      ) {
        setMaxItemsReached(true);
        setSubmissionNotice({
          message: <W>subscription.response.success</W>,
          className: "success",
        });
        return; // Early return to prevent adding more items
      }
      dispatch({
        type: actionType,
        itemId,
        productId,
        title,
        weight,
        unit_type,
        pieces_per_vac,
        subscription_amount,
      });
    },
    [selectedItems, maxQtyMsgVisible]
  ); // Add all dependencies here

  /**
   * Handle submitted items
   */
  const handleSubmit = () => {
    if (maxItemsReached) {
      navigate(paths.steps["period"] || "/"); // Fallback to root if path is undefined
    } else {
      setSubmissionNotice({
        message: `Please add ${
          selectedItems.maxItems - selectedItems.totalItems
        } more item(s) to proceed.`,
        className: "error",
      });
    }
  };

  const handleCountBoxClick = () => {
    setIsCountBoxOpen(!isCountBoxOpen);
  };

  // Box Precentage
  const fillPercentage =
    (selectedItems.totalItems / selectedItems.maxItems) * 100;
  const boxFill = {
    height: `${Math.min(fillPercentage, 100)}%`, // Ensure it doesn't exceed 100%
  };

  // Progress bar calculation based on item selection
  const progressBarPercentage =
    25 + (selectedItems.totalItems / selectedItems.maxItems) * 50; // Increase from 50% to 75%
  const progressBarWidth = {
    width: `${progressBarPercentage}%`,
  };

  if (!data.length) {
    return (
      <div className="subscription-box">
        <div className="container pt-5 pb-5">
          <div className="row">
            <div className="col-12">
              <SubscriptionCustomizeLoader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="subscription-box">
        <div className="progress-bar">
          <div className="fill" style={progressBarWidth}></div>
        </div>

        {submissionNotice.message && (
          <div className={`response ${submissionNotice.className}`}>
            {submissionNotice.message}
          </div>
        )}

        <div className="container">
          <div className="mt-4 text-center">
            <h2 className="heading">
              {" "}
              {isResetting
                ? t("subscription.customize.page.title-update", {})
                : t("subscription.customize.page.title", {
                    num: selectedItems.maxItems,
                  })}
            </h2>
          </div>
        </div>

        <div className="container farmshop-items">
          <div className="row">
            <div
              className={`pack-overview ${
                (() => {
                  const hasRegularItems = Object.values(selectedItems.items || {}).length > 0;
                  const subscriptionItems = localStorageUtil.getItem("subscription")?.items || {};
                  const hasGiftItems = Object.values(subscriptionItems).some(item => item?.is_gift === true);
                  return hasRegularItems || hasGiftItems ? "has-items" : "empty";
                })()
              } ${isCountBoxOpen ? "open" : ""}`}
            >
              <div className="cta">
                <div className="count" onClick={handleCountBoxClick}>
                  <span className="ico">
                    <span className="ico-fill" style={boxFill}></span>
                  </span>
                  <span className="numbers">
                    {`${selectedItems.totalItems}` +
                      " " +
                      t("subscription.of") +
                      " " +
                      `${selectedItems.maxItems}`}
                  </span>
                  <span className="ml-1 d-none d-md-block">
                    <W>subscription.products_chosen</W>
                  </span>
                </div>

                {maxItemsReached ? (
                  <button className="btn btn--red" onClick={handleSubmit}>
                    {t("subscription." + type + ".cta")}
                  </button>
                ) : (
                  <button className="btn btn--disable">
                    {" "}
                    {t("subscription." + type + ".cta")}{" "}
                  </button>
                )}
              </div>
              <div className="overview-items">
                <div className="overview-items-holder">
                  <div
                    className={`subscription__cta2 customizeCta loading ${
                      (() => {
                        const hasRegularItems = Object.values(selectedItems.items || {}).length > 0;
                        const subscriptionItems = localStorageUtil.getItem("subscription")?.items || {};
                        const hasGiftItems = Object.values(subscriptionItems).some(item => item?.is_gift === true);
                        return hasRegularItems || hasGiftItems ? "has-items" : "empty";
                      })()
                    }`}
                  >
                    <div className="title m-0">
                      <W>subscription.overview.title</W>
                    </div>
                    {
                    /* {Object.values(selectedItems.items).length == 0 && (
                      <div className="empty-cart">
                        <W>subscription.overview.empty_cart</W>
                      </div>
                    )} */}
                    {/* Show all gift items (full and partial) separately with special styling */}
                    {(() => {
                      // Read all gift items from localStorage and selectedItems
                      const subscriptionItems = localStorageUtil.getItem("subscription")?.items || {};
                      const fullGiftItems = Object.entries(subscriptionItems).filter(
                        ([itemId, item]) => item?.is_gift === true && item?.is_partial_gift !== true
                      );
                      
                      // Get partial gift items from selectedItems - show only the gift portion
                      const partialGiftItems = Object.entries(selectedItems.items)
                        .filter(([itemId, item]) => item?.is_partial_gift === true)
                        .map(([itemId, item]) => {
                          // Create a display item showing only the gift portion
                          const partialGiftAmount = parseInt(item.partial_gift_amount, 10) || 1;
                          return [itemId, { ...item, quantity: partialGiftAmount }];
                        });
                      
                      const allGiftItems = [...fullGiftItems, ...partialGiftItems];
                      
                      return allGiftItems.length > 0 ? (
                        <div className="gift-items-section">
                          {allGiftItems.map(([itemId, item]) => (
                            <div className="itm gift-item mb-2" key={`gift-${itemId}`}>
                              <div className="h-title">
                                <span className="name">
                                  <i className="icon-gift"></i>
                                  {item.title}
                                </span>
                                <span className="info">
                                  {`~${item.weight}${item.unit_type || "g"} x ${item.subscription_amount ? item.subscription_amount * item.pieces_per_vac : item.pieces_per_vac} `}{" "}
                                  <W>farmshop.overview.pcs</W>
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                    {/* Show regular items and regular portion of partial gift items */}
                    {Object.entries(selectedItems.items || {})
                      .map(([itemId, item]) => {
                        // For partial gift items, show only the regular portion (quantity - gift amount)
                        if (item.is_partial_gift) {
                          const partialGiftAmount = parseInt(item.partial_gift_amount, 10) || 1;
                          const regularQuantity = item.quantity - partialGiftAmount;
                          // Only show if there's a regular portion (quantity > gift amount)
                          if (regularQuantity <= 0) {
                            return null;
                          }
                          // Return item with regular quantity for display
                          return [itemId, { ...item, quantity: regularQuantity }];
                        }
                        // Regular items show as-is
                        return [itemId, item];
                      })
                      .filter((entry) => entry !== null)
                      .map(([itemId, item]) => (
                        <div className="itm mb-2" key={itemId}>
                          <div className="h-title">
                            <span className="name">{item.title}</span>
                            <span className="info">
                              {`~${item.weight}${item.unit_type || "g"} x ${item.subscription_amount ? item.subscription_amount * item.pieces_per_vac : item.pieces_per_vac} `}{" "}
                              <W>farmshop.overview.pcs</W>
                            </span>
                          </div>
                        <div className={`quantity  enabled`}>
                          <a
                            className={`plus-btn `}
                            role="button"
                            onClick={() => {
                              const dataItem = data.find(
                                (dataItem) =>
                                  dataItem.meatcut_product_id ===
                                  item.product_id
                              );

                              if (dataItem) {
                                handleItemSelection(
                                  dataItem.bundled_item_id,
                                  dataItem.meatcut_product_id,
                                  dataItem.max_quantity,
                                  "add",
                                  dataItem.title,
                                  dataItem.weight,
                                  dataItem.unit_type,
                                  dataItem.pieces_per_vac,
                                  dataItem.subscription_amount
                                );
                              }
                            }}
                          >
                            <i className="icon-plus"></i>
                          </a>
                          <span className="number">
                            <input
                              type="text"
                              value={item.quantity.toString() || ""}
                              disabled
                            />
                          </span>
                          <a
                            className="minus-btn"
                            role="button"
                            onClick={() => {
                              const dataItem = data.find(
                                (dataItem) =>
                                  dataItem.meatcut_product_id ===
                                  item.product_id
                              );

                              if (dataItem) {
                                handleItemSelection(
                                  dataItem.bundled_item_id,
                                  dataItem.meatcut_product_id,
                                  dataItem.max_quantity,
                                  "remove",
                                  dataItem.title,
                                  dataItem.weight,
                                  dataItem.unit_type,
                                  dataItem.pieces_per_vac,
                                  dataItem.subscription_amount
                                );
                              }
                            }}
                          >
                            <i className="icon-minus"></i>
                          </a>
                        </div>
                      </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="bundle__items">
                {/* Render animal groups and their items */}
                {animalTypes.map((animalType) => (
                  <Fragment key={animalType}>
                    <div className={`animal_group animal_group_${animalType}`}>
                      <a name={`products_${animalType}`}></a>
                      <h2 className="title m-0">
                        <span className="name">
                          {t(animalType).charAt(0).toUpperCase() +
                            t(animalType).slice(1)}
                        </span>
                        <span className="sep">-</span>
                        <span className="desc"> {t(animalType + "_desc")}</span>
                      </h2>
                      <hr
                        ref={animalGroupRefs.current[animalType]}
                        id={`animal_type_${animalType}`}
                      />
                    </div>

                    {/* Render items for the current animal type */}
                    {data
                      .filter((item) => item.animal_type === animalType)
                      .map((item) => (
                        <div
                          className={
                            "farmshop__item " +
                            (item.is_organic == 1 ? "organic" : "")
                          }
                          key={item.bundled_item_id}
                        >
                          <div className="farmshop__title">
                            <span className="title">{item.title}</span>
                          </div>
                          <figure className="img m-0">
                            {maxQtyMsgVisible[item.bundled_item_id] ? (
                              <>
                                <div className="msg">
                                  {t("subscription.response.maximum_quantity")}
                                </div>
                              </>
                            ) : (
                              ""
                            )}
                            <Image
                              src={item.image_url}
                              alt={item.title}
                              title={item.title}
                              className="img-fluid"
                            />
                          </figure>
                          <div className="farmshop__item__farmer px-2">
                            <div className="farmshop_farmer_image">
                              <img
                                src={
                                  item.animal_type === "cow" ||
                                  item.animal_type === "bioCow"
                                    ? farmersImgcow
                                    : item.animal_type === "pig"
                                    ? farmersImgpig
                                    : item.animal_type === "chicken"
                                    ? farmersImgchicken
                                    : item.animal_type === "fish"
                                    ? farmersImgfish
                                    : farmersImgcow // default fallback
                                }
                                width="100"
                                height="100"
                                alt={t("subscription.farmer_name")}
                                title={t("subscription.farmer_name")}
                              />
                            </div>
                            <div className="farmshop_farmer_name">
                              <W>farmers.{item.animal_type}</W>:{" "}
                              <W>subscription.farmer_name.{item.animal_type}</W>
                            </div>
                          </div>
                          <div className="body p-2">
                            <div className="subscription__item__info">
                              <span className="weight">
                                <span className="number">
                                  {item.subscription_amount ? (
                                    <>
                                      {item.subscription_amount}
                                      <span className="fw-normal">x</span>
                                      {item.pieces_per_vac}
                                    </>
                                  ) : (
                                    item.pieces_per_vac
                                  )}
                                </span>{" "}
                                <W>subscription.pieces</W>{" "}
                                <span>
                                  {item.weight}{" "}
                                  <W>
                                    farmshop.unit_txt.
                                    {item.unit_type ? item.unit_type : "g"}
                                  </W>
                                </span>
                              </span>

                              <span className="total">
                                {(item.subscription_amount
                                  ? item.subscription_amount *
                                    item.pieces_per_vac
                                  : item.pieces_per_vac) * item.weight}
                                <W>
                                  farmshop.unit_txt.
                                  {item.unit_type ? item.unit_type : "g"}
                                </W>
                              </span>
                            </div>
                            <div
                              className={`quantity product-${
                                item.bundled_item_id
                              } ${
                                (() => {
                                  const selectedItem = selectedItems.items[item.bundled_item_id];
                                  if (!selectedItem) return false;
                                  // For partial gift items, check if display quantity > 0
                                  if (selectedItem.is_partial_gift) {
                                    const partialGiftAmount = parseInt(selectedItem.partial_gift_amount, 10) || 1;
                                    return (selectedItem.quantity - partialGiftAmount) > 0;
                                  }
                                  return selectedItem.quantity > 0;
                                })()
                                  ? "enabled"
                                  : ""
                              }`}
                            >
                              <a
                                className={`plus-btn ${
                                  selectedItems.items[item.bundled_item_id]
                                    ?.quantity >= item.max_quantity &&
                                  item.max_quantity !== ""
                                    ? "btn--disable"
                                    : ""
                                }`}
                                role="button"
                                onClick={() =>
                                  handleItemSelection(
                                    item.bundled_item_id,
                                    item.meatcut_product_id,
                                    item.max_quantity,
                                    "add",
                                    item.title,
                                    item.weight,
                                    item.unit_type,
                                    item.pieces_per_vac,
                                    item.subscription_amount
                                  )
                                }
                              >
                                <i className="icon-plus"></i>
                              </a>
                              <span className="number">
                                <input
                                  type="text"
                                  value={
                                    (() => {
                                      const selectedItem = selectedItems.items[item.bundled_item_id];
                                      if (!selectedItem) return "";
                                      // For partial gift items, show quantity - partial_gift_amount
                                      // If result is 0, show empty (so it acts like a normal add button)
                                      if (selectedItem.is_partial_gift) {
                                        const partialGiftAmount = parseInt(selectedItem.partial_gift_amount, 10) || 1;
                                        const displayQuantity = selectedItem.quantity - partialGiftAmount;
                                        return displayQuantity > 0 ? displayQuantity.toString() : "";
                                      }
                                      return selectedItem.quantity.toString();
                                    })()
                                  }
                                  disabled
                                />
                              </span>
                              <a
                                className="minus-btn"
                                role="button"
                                onClick={() =>
                                  handleItemSelection(
                                    item.bundled_item_id,
                                    item.meatcut_product_id,
                                    item.max_quantity,
                                    "remove",
                                    item.title,
                                    item.weight,
                                    item.unit_type,
                                    item.pieces_per_vac,
                                    item.subscription_amount
                                  )
                                }
                              >
                                <i className="icon-minus"></i>
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default flatstore.connect(["subscription"])(CustomizeSubscription);
